import React, { useState, useCallback, useMemo, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell } from "@fortawesome/free-solid-svg-icons";
import { parseISO } from "date-fns";
import { Notification, Mover } from "@/types";
import { formatDateWithOrdinal } from "@/lib/utils";
import {
  useGetNotificationsQuery,
  useMarkAllNotificationsAsReadMutation,
} from "@/store/mover/apis";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface NotificationsMenuProps {
  movers: Mover[];
}

const NotificationsMenu: React.FC<NotificationsMenuProps> = ({ movers }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [allNotifications, setAllNotifications] = useState<Notification[]>([]);
  const [hasUnread, setHasUnread] = useState(false);
  const [isBellAnimating, setIsBellAnimating] = useState(false);
  const [markAllAsRead] = useMarkAllNotificationsAsReadMutation();

  // Create a map of mover IDs to names for efficient lookups
  const moversMap = useMemo(() => {
    return movers.reduce((acc, mover) => {
      acc[mover.id] = mover.name;
      return acc;
    }, {} as Record<number, string>);
  }, [movers]);

  const {
    data: notifications = {
      data: [],
      pagination: {
        current_page: 1,
        total_pages: 1,
        total_count: 0,
      },
    },
    isLoading,
    isFetching,
  } = useGetNotificationsQuery(
    { page: currentPage, per_page: 10 },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  // Update notifications when data changes
  React.useEffect(() => {
    if (notifications.data.length > 0) {
      setAllNotifications((prev) => {
        // Always replace data for page 1
        if (currentPage === 1) {
          return notifications.data;
        }
        // For subsequent pages, append only if we don't already have these notifications
        const newNotifications = notifications.data.filter(
          (newNotif) =>
            !prev.some((existingNotif) => existingNotif.id === newNotif.id)
        );
        return [...prev, ...newNotifications];
      });

      // Update unread status
      const hasUnread = notifications.data.some((n) => !n.read);
      setHasUnread(hasUnread);
    }
  }, [notifications.data, currentPage]);

  // Add event listener for new notifications
  useEffect(() => {
    const handleNewNotification = (event: CustomEvent<Notification>) => {
      setAllNotifications((prev) => [event.detail, ...prev]);
      setHasUnread(true);
      setIsBellAnimating(true);
      setTimeout(() => setIsBellAnimating(false), 1000);
    };

    window.addEventListener(
      "new-notification",
      handleNewNotification as EventListener
    );
    return () => {
      window.removeEventListener(
        "new-notification",
        handleNewNotification as EventListener
      );
    };
  }, []);

  const handleLoadMore = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const hasMorePages =
        notifications.pagination.current_page <
        notifications.pagination.total_pages;

      if (!isLoading && !isFetching && hasMorePages) {
        setCurrentPage((prev) => prev + 1);
      }
    },
    [isLoading, isFetching, notifications.pagination]
  );

  const handleBellClick = async () => {
    // Only mark as read if there are unread notifications
    if (!hasUnread) return;

    // Set a timeout to mark all notifications as read after 2000ms
    setTimeout(async () => {
      try {
        await markAllAsRead({}).unwrap();
        // Update local state to mark all notifications as read
        setAllNotifications((prev) =>
          prev.map((notification) => ({
            ...notification,
            read: true,
          }))
        );
        setHasUnread(false);
      } catch (error) {
        console.error("Failed to mark notifications as read:", error);
      }
    }, 2000);
  };

  const hasMore =
    notifications.pagination.current_page <
    notifications.pagination.total_pages;

  const showMoverName = movers.length > 1;

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (open) {
          handleBellClick();
        }
      }}
    >
      <DropdownMenuTrigger asChild>
        <div className="relative cursor-pointer">
          <FontAwesomeIcon
            icon={faBell}
            className={`w-6 h-6 transition-transform duration-300 ${
              isBellAnimating ? "animate-bell-ring" : ""
            }`}
          />
          {hasUnread && (
            <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full" />
          )}
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-96 absolute top-2 -right-6 shadow-md p-0 border border-stone-200">
        <DropdownMenuLabel className="text-sm font-bold bg-stone-100 py-2 border-b border-stone-200">
          Notifications
        </DropdownMenuLabel>
        {/* <DropdownMenuSeparator className="bg-stone-300" /> */}
        <div className="max-h-[400px] overflow-y-auto">
          {isLoading && currentPage === 1 ? (
            <DropdownMenuItem>
              <div className="flex items-center">
                <span>Loading...</span>
              </div>
            </DropdownMenuItem>
          ) : allNotifications.length > 0 ? (
            <>
              {allNotifications.map((notification, index) => {
                const moverName = moversMap[notification.mover_id];
                return (
                  <div
                    key={notification.id}
                    className="divide-y divide-stone-100"
                  >
                    <DropdownMenuItem
                      className={`cursor-pointer focus:bg-white hover:bg-white ${
                        !notification.read ? "bg-slate-100" : ""
                      }`}
                    >
                      <div className="flex items-start gap-2 w-full px-1">
                        <div
                          className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                            notification.read ? "bg-stone-100" : "bg-blue-500"
                          }`}
                        />
                        <div className="flex flex-col flex-grow">
                          <div className="flex justify-between items-center mt-1">
                            <span className="text-xs text-stone-500">
                              {formatDateWithOrdinal(
                                parseISO(notification.created_at),
                                {
                                  showDayOfWeek: false,
                                  showTime: true,
                                }
                              )}
                            </span>
                            {showMoverName && moverName && (
                              <span className="text-xs text-stone-500">
                                {moverName}
                              </span>
                            )}
                          </div>
                          <div className={`font-semibold text-sm`}>
                            {notification.title}
                          </div>
                          <div className="text-xs text-slate-600">
                            {notification.description}
                          </div>
                        </div>
                      </div>
                    </DropdownMenuItem>
                    {index < allNotifications.length - 1 && (
                      <DropdownMenuSeparator />
                    )}
                  </div>
                );
              })}
              {hasMore && (
                <DropdownMenuItem
                  className="cursor-pointer hover:bg-stone-100 flex justify-center"
                  onClick={handleLoadMore}
                >
                  <span className="text-sm text-blue-600 hover:text-blue-800">
                    {isFetching ? "Loading more..." : "Load More"}
                  </span>
                </DropdownMenuItem>
              )}
            </>
          ) : (
            <DropdownMenuItem>
              <div className="flex items-center">
                <span>No notifications</span>
              </div>
            </DropdownMenuItem>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationsMenu;
